// FAMILY-EXT: модал управления внешними хранилищами (Synology WebDAV и т.п.).
// Только админ может править/удалять (бэк policy). Список + create/edit
// форма + удаление.

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Header, Icon, Input, Modal, Segment } from 'semantic-ui-react';

import api from '../../api';
import { getAccessToken } from '../../utils/access-token-storage';

import styles from './ExternalStoragesModal.module.scss';

const EMPTY_FORM = {
  id: null,
  name: '',
  url: '',
  username: '',
  password: '',
  scopeProjectIds: [],
  scopeBoardIds: [],
};

const ExternalStoragesModal = React.memo(({ projects, isAdmin, onClose }) => {
  const [t] = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // null = list mode; obj = form mode

  const authHeaders = useCallback(() => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await api.browseStorages(authHeaders());
      setItems(body.items || []);
    } catch (e) {
      setError(e && e.message ? e.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAddClick = useCallback(() => {
    setForm({ ...EMPTY_FORM });
  }, []);

  const handleEditClick = useCallback((item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      url: item.url || '',
      username: item.username || '',
      password: '', // не возвращаем пароль с сервера, пустой = "оставить"
      scopeProjectIds: Array.isArray(item.scopeProjectIds) ? item.scopeProjectIds : [],
      scopeBoardIds: Array.isArray(item.scopeBoardIds) ? item.scopeBoardIds : [],
    });
  }, []);

  const handleCancelClick = useCallback(() => {
    setForm(null);
  }, []);

  const handleDeleteClick = useCallback(
    async (item) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Удалить хранилище "${item.name}"?`)) return;
      try {
        await api.deleteStorage(item.id, authHeaders());
        await loadList();
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert(`Ошибка удаления: ${e.message || e}`);
      }
    },
    [loadList, authHeaders],
  );

  const handleFieldChange = useCallback((name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const handleProjectToggle = useCallback((projectId) => {
    setForm((f) => {
      const has = f.scopeProjectIds.includes(projectId);
      return {
        ...f,
        scopeProjectIds: has
          ? f.scopeProjectIds.filter((x) => x !== projectId)
          : [...f.scopeProjectIds, projectId],
      };
    });
  }, []);

  const handleBoardToggle = useCallback((boardId) => {
    setForm((f) => {
      const has = f.scopeBoardIds.includes(boardId);
      return {
        ...f,
        scopeBoardIds: has
          ? f.scopeBoardIds.filter((x) => x !== boardId)
          : [...f.scopeBoardIds, boardId],
      };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim() || !form.url.trim() || !form.username.trim()) {
      // eslint-disable-next-line no-alert
      window.alert('Заполните: название, URL, логин');
      return;
    }
    if (!form.id && !form.password) {
      // eslint-disable-next-line no-alert
      window.alert('При создании пароль обязателен');
      return;
    }
    const data = {
      name: form.name.trim(),
      url: form.url.trim(),
      username: form.username.trim(),
      scopeProjectIds: form.scopeProjectIds,
      scopeBoardIds: form.scopeBoardIds,
    };
    if (form.password) data.password = form.password;

    try {
      if (form.id) {
        await api.updateStorage(form.id, data, authHeaders());
      } else {
        await api.createStorage(data, authHeaders());
      }
      setForm(null);
      await loadList();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Ошибка сохранения: ${e.message || e}`);
    }
  }, [form, loadList, authHeaders]);

  return (
    <Modal open closeIcon size="small" centered={false} onClose={onClose}>
      <Modal.Header>{t('common.externalStorages', { context: 'title' })}</Modal.Header>
      <Modal.Content scrolling>
        {!isAdmin && (
          <div className={styles.notice}>
            Только администратор может создавать и редактировать хранилища.
          </div>
        )}
        {form === null ? (
          <>
            {loading && <div className={styles.center}>Загрузка…</div>}
            {error && <div className={styles.error}>{error}</div>}
            {!loading && !error && items.length === 0 && (
              <div className={styles.empty}>Нет хранилищ. Добавь первое.</div>
            )}
            {!loading && items.length > 0 && (
              <ul className={styles.list}>
                {items.map((item) => (
                  <li key={item.id} className={styles.item}>
                    <div className={styles.itemMain}>
                      <div className={styles.itemName}>{item.name}</div>
                      <div className={styles.itemMeta}>
                        {item.url}
                        {' • '}
                        {scopeSummary(item, projects)}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className={styles.itemActions}>
                        <Button size="mini" onClick={() => handleEditClick(item)}>
                          <Icon name="pencil" /> Изменить
                        </Button>
                        <Button size="mini" negative onClick={() => handleDeleteClick(item)}>
                          <Icon name="trash" /> Удалить
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {isAdmin && (
              <Button primary onClick={handleAddClick}>
                <Icon name="plus" /> Добавить хранилище
              </Button>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Field>
              <label>Название (отобразится в списке прикрепления)</label>
              <Input
                value={form.name}
                onChange={(_, d) => handleFieldChange('name', d.value)}
                placeholder="Например: Рабочая Synology"
              />
            </Form.Field>
            <Form.Field>
              <label>WebDAV URL</label>
              <Input
                value={form.url}
                onChange={(_, d) => handleFieldChange('url', d.value)}
                placeholder="https://dsm-dav.example.com"
              />
            </Form.Field>
            <Form.Field>
              <label>Логин</label>
              <Input
                value={form.username}
                onChange={(_, d) => handleFieldChange('username', d.value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Пароль {form.id && '(оставь пустым чтобы не менять)'}</label>
              <Input
                type="password"
                value={form.password}
                onChange={(_, d) => handleFieldChange('password', d.value)}
              />
            </Form.Field>
            <Header as="h4">Область применения</Header>
            <p className={styles.scopeHelp}>
              Отметь проекты целиком ИЛИ отдельные доски. Доска доступна если хотя бы
              одно условие выполнено.
            </p>
            {projects.map((p) => (
              <Segment key={p.id} className={styles.projectGroup}>
                <Form.Checkbox
                  label={`Весь проект «${p.name}»`}
                  checked={form.scopeProjectIds.includes(p.id)}
                  onChange={() => handleProjectToggle(p.id)}
                />
                {p.boards && p.boards.length > 0 && (
                  <div className={styles.boardList}>
                    {p.boards.map((b) => (
                      <Form.Checkbox
                        key={b.id}
                        label={b.name}
                        checked={form.scopeBoardIds.includes(b.id)}
                        onChange={() => handleBoardToggle(b.id)}
                        disabled={form.scopeProjectIds.includes(p.id)}
                      />
                    ))}
                  </div>
                )}
              </Segment>
            ))}
            <div className={styles.formActions}>
              <Button positive type="submit">
                {form.id ? 'Сохранить' : 'Создать'}
              </Button>
              <Button type="button" onClick={handleCancelClick}>
                Отмена
              </Button>
            </div>
          </Form>
        )}
      </Modal.Content>
    </Modal>
  );
});

// Краткое описание scope для отображения в списке.
function scopeSummary(item, projects) {
  const pIds = item.scopeProjectIds || [];
  const bIds = item.scopeBoardIds || [];
  if (pIds.length === 0 && bIds.length === 0) return 'не привязано';
  const parts = [];
  if (pIds.length > 0) {
    const names = pIds
      .map((id) => projects.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => p.name);
    parts.push(`${pIds.length} ${pluralize(pIds.length, ['проект', 'проекта', 'проектов'])}${names.length ? `: ${names.join(', ')}` : ''}`);
  }
  if (bIds.length > 0) {
    parts.push(`${bIds.length} ${pluralize(bIds.length, ['доска', 'доски', 'досок'])}`);
  }
  return parts.join(', ');
}

function pluralize(n, [one, few, many]) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

ExternalStoragesModal.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  projects: PropTypes.array.isRequired,
  /* eslint-enable react/forbid-prop-types */
  isAdmin: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ExternalStoragesModal;
